import React, { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash-es/isEqual';
import throttle from 'lodash-es/throttle';
import { prepareFilterDataForSearch } from 'features/queue/grid/helpers';
import { setSearchSrParams, selectColumnsOrder } from 'features/queue/slice';
import useGridApi from 'common/providers/AgGridProvider/hooks/useGridApi';
import useGridLoading from 'common/providers/AgGridProvider/hooks/useGridLoading';
import useQueueData from 'features/queue/newGrid/hooks/useQueueData';
import { GetRowsParams, ServerRowModelDataSource } from 'common/providers/AgGridProvider';
import useStoreGridState from 'features/queue/newGrid/hooks/useStoreGridState';
import { CACHE_BLOCK_SIZE } from 'features/queue/newGrid/consts';

interface Params {
    filterModelRef: React.MutableRefObject<[]>;
    queueSearchTextRef: React.MutableRefObject<string>;
}

const useServerRowModelDataSource = (): (params: Params) => void => {
    const dispatch = useDispatch();
    const [api] = useGridApi();
    const [isGridLoading] = useGridLoading();
    const { fetchQueueData, data: queueData } = useQueueData();
    const columnsOrder = useSelector(selectColumnsOrder, isEqual);
    const { getGridState } = useStoreGridState();
    const isFirstFetch = useRef<boolean>(true);

    const refreshCells = useRef(
        throttle((gridApi) => {
            if (gridApi && !gridApi.isDestroyed()) {
                gridApi.refreshCells({ force: true });
            }
        }, 500, {
            leading: false,
            trailing: true,
        })
    ).current;

    useEffect(() => {
        if (queueData?.serviceRequests.length === 0) {
            api.current?.showNoRowsOverlay?.();
        }
    }, [queueData, api]);

    return useCallback(({
        filterModelRef,
        queueSearchTextRef,
    }: Params) => {
        const datasource: ServerRowModelDataSource = {
            getRows: async (params: GetRowsParams) => {
                const { request, api } = params;
                const { sortModel } = request;
                let { startRow, endRow } = request;

                if (isFirstFetch.current) {
                    isFirstFetch.current = false;
                    const gridState = getGridState();
                    if (gridState) {
                        const { pageNumber, pageSize } = gridState;
                        const blockSize = pageNumber * pageSize;
                        startRow = Math.floor(blockSize / CACHE_BLOCK_SIZE) * CACHE_BLOCK_SIZE;
                        endRow = startRow + CACHE_BLOCK_SIZE;
                    }
                }

                const convertedFilterModel = prepareFilterDataForSearch(
                    filterModelRef.current,
                    queueSearchTextRef?.current,
                );

                const searchSrParams = {
                    endRow,
                    startRow,
                    sortModel,
                    filterModel: convertedFilterModel,
                    columnsOrder,
                };

                try {
                    if (isGridLoading.current) {
                        api.showLoadingOverlay();
                    }

                    const { countTotal, serviceRequests } = await fetchQueueData(searchSrParams);

                    dispatch(setSearchSrParams(searchSrParams));

                    if (serviceRequests.length) {
                        api.hideOverlay();
                    }

                    params.success({
                        rowData: serviceRequests,
                        rowCount: countTotal,
                    });

                    refreshCells(api);
                } catch (ex) {
                    params.fail();
                    console.error('failed to fetch queue data', ex);
                }
            },
        };

        api.current.setGridOption('serverSideDatasource', datasource);
    }, [
        api,
        columnsOrder,
        dispatch,
        fetchQueueData,
        isGridLoading,
        refreshCells,
        getGridState,
    ]);
}

export default useServerRowModelDataSource;